
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";

export default function Sidebar({ isExpanded, setIsExpanded }) {
  const sidebarRef = useRef(null);
  const navigate = useNavigate();
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [permissions, setPermissions] = useState([]);

  useEffect(() => {
    const role = localStorage.getItem("userRole");
    setUserRole(role);

    const storedPermission = localStorage.getItem("permission");

    try {
      if (storedPermission) {
        const parsedPermission = JSON.parse(storedPermission);

        if (Array.isArray(parsedPermission)) {
          setPermissions(parsedPermission);
        } else if (typeof parsedPermission === "string") {
          setPermissions([parsedPermission]);
        }
      } else {
        setPermissions([]);
      }
    } catch (error) {
      console.error("Error parsing permissions:", error);

      if (storedPermission) {
        setPermissions(
          storedPermission
            .split(",")
            .map((permission) => permission.trim())
            .filter(Boolean),
        );
      }
    }
  }, []);

  const hasPermission = (pageCode) => {
    if (userRole === "SuperAdmin") {
      return true;
    }

    return permissions.includes(pageCode);
  };

  const toggleSidebar = (event) => {
    setIsExpanded(!isExpanded);
    event?.stopPropagation();
  };

  const Logout = (event) => {
    event.preventDefault();

    localStorage.removeItem("token");
    localStorage.removeItem("userRole");
    localStorage.removeItem("permission");

    navigate("/");
  };

  useEffect(() => {
    const handleMouseEnter = () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
        setHoverTimeout(null);
      }

      setIsExpanded(true);
    };

    const handleMouseLeave = () => {
      const timeout = setTimeout(() => {
        setIsExpanded(false);
      }, 300);

      setHoverTimeout(timeout);
    };

    const sidebar = sidebarRef.current;

    if (sidebar) {
      sidebar.addEventListener("mouseenter", handleMouseEnter);
      sidebar.addEventListener("mouseleave", handleMouseLeave);
    }

    return () => {
      if (sidebar) {
        sidebar.removeEventListener("mouseenter", handleMouseEnter);
        sidebar.removeEventListener("mouseleave", handleMouseLeave);
      }

      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [setIsExpanded, hoverTimeout]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setIsExpanded(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [setIsExpanded]);

  return (
    <div className="wrapper">
      <aside
        id="sidebar"
        ref={sidebarRef}
        className={isExpanded ? "expand" : ""}
      >
        <div className="d-flex">
          <button className="toggle-btn" type="button" onClick={toggleSidebar}>
            <i className="lni lni-grid-alt"></i>
          </button>

          <div className="sidebar-logo">
            <Link to="/Dashboard">TrackMaster Hub</Link>
          </div>
        </div>

        <ul className="sidebar-nav">
          <li className="sidebar-group">
            <div className="group-title">Home</div>

            <ul className="group-items">
              <li className="sidebar-item">
                <Link to="/Dashboard" className="sidebar-link">
                  <i className="fa-brands fa-atlassian"></i>
                  <span>Dashboard</span>
                </Link>
              </li>

              {hasPermission("TRIPS") && (
                <li className="sidebar-item">
                  <Link to="/TripsDashboard" className="sidebar-link">
                    <i className="fa-solid fa-truck-arrow-right"></i>
                    <span>Trips</span>
                  </Link>
                </li>
              )}

              {hasPermission("REPORTS") && (
                <li className="sidebar-item">
                  <Link to="/Report" className="sidebar-link">
                    <i className="fa-solid fa-chart-column"></i>
                    <span>Analytics & Reports</span>
                  </Link>
                </li>
              )}
            </ul>
          </li>

          {(hasPermission("GPIO") ||
            hasPermission("ALERTS") ||
            hasPermission("CONNECTIVITY")) && (
            <li className="sidebar-group">
              <div className="group-title">Actions & Events</div>

              <ul className="group-items">
                {hasPermission("GPIO") && (
                  <li className="sidebar-item">
                    <Link to="/ImmobilizeMobilize" className="sidebar-link">
                      <i className="fa-brands fa-buffer"></i>
                      <span>Mobilize/Immobilize</span>
                    </Link>
                  </li>
                )}

                {hasPermission("ALERTS") && (
                  <li className="sidebar-item">
                    <Link to="/Alerts" className="sidebar-link">
                      <i className="fa-solid fa-braille"></i>
                      <span>Active Alerts</span>
                    </Link>
                  </li>
                )}

                {hasPermission("CONNECTIVITY") && (
                  <li className="sidebar-item">
                    <Link to="/Connectivity" className="sidebar-link">
                      <i className="fa-solid fa-circle-nodes"></i>
                      <span>Connectivity</span>
                    </Link>
                  </li>
                )}
              </ul>
            </li>
          )}

          {(hasPermission("DEVICES") ||
            hasPermission("PROVISIONING") ||
            hasPermission("PARAMETER_OTA") ||
            hasPermission("FOTA")) && (
            <li className="sidebar-group">
              <div className="group-title">Device Deployment</div>

              <ul className="group-items">
                {hasPermission("DEVICES") && (
                  <li className="sidebar-item">
                    <Link to="/DeviceCondition" className="sidebar-link">
                      <i className="fa-brands fa-renren"></i>
                      <span>Device Dashboard</span>
                    </Link>
                  </li>
                )}

                {hasPermission("PROVISIONING") && (
                  <li className="sidebar-item">
                    <Link to="/Provisioning" className="sidebar-link">
                      <i className="fa-brands fa-mizuni"></i>
                      <span>Onboarding</span>
                    </Link>
                  </li>
                )}

                {hasPermission("PARAMETER_OTA") && (
                  <li className="sidebar-item">
                    <Link to="/ParameterOverAir" className="sidebar-link">
                      <i className="fa-brands fa-codepen"></i>
                      <span>Parameter-Over-Air</span>
                    </Link>
                  </li>
                )}

                {hasPermission("FOTA") && (
                  <li className="sidebar-item">
                    <Link to="/FirmwareOverAir" className="sidebar-link">
                      <i className="fa-brands fa-unity"></i>
                      <span>Firmware-Over-Air</span>
                    </Link>
                  </li>
                )}

                {hasPermission("FOTA") && (
                  <li className="sidebar-item">
                    <Link to="/OverAirUpdatesLogs" className="sidebar-link">
                      <i className="fa-brands fa-stumbleupon-circle"></i>
                      <span>Over-Air Updates Logs</span>
                    </Link>
                  </li>
                )}
              </ul>
            </li>
          )}

          {hasPermission("ECU_FOTA") && (
            <li className="sidebar-group">
              <div className="group-title">ECU Over Air Updates</div>

              <ul className="group-items">
                <li className="sidebar-item">
                  <Link to="/RollOut" className="sidebar-link">
                    <i className="fa-solid fa-feather"></i>
                    <span>RollOut</span>
                  </Link>
                </li>

                <li className="sidebar-item">
                  <Link to="/Approvals" className="sidebar-link">
                    <i className="fa-solid fa-bolt"></i>
                    <span>Approvals</span>
                  </Link>
                </li>

                <li className="sidebar-item">
                  <Link to="/ECUDeploymentStatus" className="sidebar-link">
                    <i className="fa-solid fa-snowflake"></i>
                    <span>Deployment Status</span>
                  </Link>
                </li>

                <li className="sidebar-item">
                  <Link to="/ECUOverAirUpdatesLogs" className="sidebar-link">
                    <i className="fa-brands fa-squarespace"></i>
                    <span>ECU Over-Air Updates Logs</span>
                  </Link>
                </li>
              </ul>
            </li>
          )}

          {(hasPermission("AOI") || userRole === "SuperAdmin") && (
            <li className="sidebar-group">
              <div className="group-title">Geo Services</div>

              <ul className="group-items">
                {hasPermission("AOI") && (
                  <li className="sidebar-item">
                    <Link to="/POIDashboard" className="sidebar-link">
                      <i className="fa-solid fa-tower-broadcast"></i>
                      <span>AOI</span>
                    </Link>
                  </li>
                )}

                {userRole === "SuperAdmin" && (
                  <li className="sidebar-item">
                    <Link to="/Dashboard" className="sidebar-link">
                      <i className="fa-solid fa-square-arrow-up-right"></i>
                      <span>Routes</span>
                    </Link>
                  </li>
                )}

                {userRole === "SuperAdmin" && (
                  <li className="sidebar-item">
                    <Link to="/Dashboard" className="sidebar-link">
                      <i className="fa-solid fa-location-arrow"></i>
                      <span>Activity</span>
                    </Link>
                  </li>
                )}
              </ul>
            </li>
          )}

          <li className="sidebar-group">
            <div className="group-title">Manage</div>

            <ul className="group-items">
              {hasPermission("VEHICLES") && (
                <li className="sidebar-item">
                  <Link to="/VehiclesTable" className="sidebar-link">
                    <i className="fa-solid fa-truck-moving"></i>
                    <span>Vehicles</span>
                  </Link>
                </li>
              )}

              {userRole === "SuperAdmin" && (
                <li className="sidebar-item">
                  <Link to="/BatteryDashboard" className="sidebar-link">
                    <i className="fa-brands fa-slack"></i>
                    <span>BatteryDashboard</span>
                  </Link>
                </li>
              )}

              {userRole === "SuperAdmin" && (
                <li className="sidebar-item">
                  <Link to="/Dashboard" className="sidebar-link">
                    <i className="fa-solid fa-users-rays"></i>
                    <span>Drivers</span>
                  </Link>
                </li>
              )}

              {hasPermission("USER_MANAGEMENT") && (
                <li className="sidebar-item">
                  <Link to="/UserManagement" className="sidebar-link">
                    <i className="fa-solid fa-users"></i>
                    <span>User Management</span>
                  </Link>
                </li>
              )}

              <li className="sidebar-item">
                <Link to="/Account" className="sidebar-link">
                  <i className="fa-solid fa-clipboard-user"></i>
                  <span>Account</span>
                </Link>
              </li>

              {userRole === "SuperAdmin" && (
                <li className="sidebar-item">
                  <Link to="/PermissionDashboard" className="sidebar-link">
                    <i className="fa-brands fa-shopware"></i>
                    <span>PermissionDashboard</span>
                  </Link>
                </li>
              )}
            </ul>
          </li>
        </ul>

        <div className="sidebar-footer">
          <a href="/" className="sidebar-link" onClick={Logout}>
            <i className="lni lni-exit"></i>
            <span>Logout</span>
          </a>
        </div>
      </aside>
    </div>
  );
}
